import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import Login from "@/pages/Login";
import MagicLogin from "@/pages/MagicLogin";
import Register from "@/pages/Register";
import ChoosePlan from "@/pages/ChoosePlan";
import RedeemAccess from "@/pages/RedeemAccess";
import { UpgradeDialogHost } from "@/components/UpgradeDialog";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/founder/Dashboard";
import Metrics from "@/pages/founder/Metrics";
import StructureNotes from "@/pages/founder/StructureNotes";
import UpdatesList from "@/pages/founder/UpdatesList";
import VerifyBadge from "@/pages/VerifyBadge";
import UpdateComposer from "@/pages/founder/UpdateComposer";
import Investors from "@/pages/founder/Investors";
import Delivery from "@/pages/founder/Delivery";
import Settings from "@/pages/founder/Settings";
import Documents from "@/pages/founder/Documents";
import Templates from "@/pages/founder/Templates";
import Integrations from "@/pages/founder/Integrations";
import Requests from "@/pages/founder/Requests";
import DocumentBuilder from "@/pages/founder/DocumentBuilder";
import DocumentBuilderEditor from "@/pages/founder/DocumentBuilderEditor";
import SignerDashboard from "@/pages/founder/SignerDashboard";
import InvestorImpact from "@/pages/founder/InvestorImpact";
import SweatEquity from "@/pages/founder/SweatEquity";
import Billing from "@/pages/founder/Billing";
import Fundraising from "@/pages/founder/Fundraising";
import InvestorDirectory from "@/pages/founder/InvestorDirectory";
import CapTable from "@/pages/founder/CapTable";
import DeckLinks from "@/pages/founder/DeckLinks";
import Rounds from "@/pages/founder/Rounds";
import DataRooms from "@/pages/founder/DataRooms";
import AsksQA from "@/pages/founder/AsksQA";
import Team from "@/pages/founder/Team";
import Funds from "@/pages/founder/Funds";
import FundDetail from "@/pages/founder/FundDetail";
import DiligenceRoom from "@/pages/founder/DiligenceRoom";
import Advisor from "@/pages/founder/Advisor";
import PublicDiligence from "@/pages/PublicDiligence";
import Portfolio from "@/pages/investor/Portfolio";
import FundPositions from "@/pages/investor/FundPositions";
import FundPositionDetail from "@/pages/investor/FundPositionDetail";
import ImpactHeatmap from "@/pages/investor/ImpactHeatmap";
import CompanyHealth from "@/pages/investor/CompanyHealth";
import UpdateReaderPage from "@/pages/investor/UpdateReaderPage";
import InvestorAccount from "@/pages/investor/Account";
import LpInvite from "@/pages/LpInvite";
import FundManagers from "@/pages/marketing/FundManagers";
import SignDocument from "@/pages/SignDocument";
import SharedUpdate from "@/pages/SharedUpdate";
import DeckViewer from "@/pages/DeckViewer";
import PublicDataRoom from "@/pages/PublicDataRoom";

import Pricing from "@/pages/marketing/Pricing";
import ProductDetail from "@/pages/marketing/ProductDetail";
import About from "@/pages/marketing/About";
import Customers from "@/pages/marketing/Customers";
import Tour from "@/pages/marketing/Tour";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail, { VerifyEmailGate } from "@/pages/VerifyEmail";
import Careers from "@/pages/marketing/Careers";
import Contact from "@/pages/marketing/Contact";
import Docs from "@/pages/marketing/Docs";
import Guides from "@/pages/marketing/Guides";
import GuideDetail from "@/pages/marketing/GuideDetail";
import Security from "@/pages/marketing/Security";
import OptionCalculator from "@/pages/marketing/OptionCalculator";
import EmployeeEquity from "@/pages/EmployeeEquity";
import Status from "@/pages/marketing/Status";
import Privacy from "@/pages/marketing/Privacy";
import Terms from "@/pages/marketing/Terms";
import { PaymentSuccess, PaymentCancel } from "@/pages/marketing/Payment";
import AdminDashboard from "@/pages/admin/AdminDashboard";

const FullLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <Loader2 className="h-6 w-6 animate-spin text-primary" />
  </div>
);

const homeFor = (u) => {
  const redirect = sessionStorage.getItem("tenx_post_auth_redirect");
  if (redirect && u.role === "investor") return redirect;
  if (u.role === "founder") {
    const redeem = sessionStorage.getItem("tenx_redeem_code");
    if (redeem) return `/redeem/${redeem}`;
    return sessionStorage.getItem("tenx_choose_plan") ? "/choose-plan" : "/app";
  }
  return u.role === "admin" ? "/admin" : "/portal";
};

function Protected({ role, children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to={homeFor(user)} replace />;
  if (user.role === "investor" && user.email_verified === false) return <VerifyEmailGate />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (user) return <Navigate to={homeFor(user)} replace />;
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homeFor(user)} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <UpgradeDialogHost />
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/magic-login" element={<MagicLogin />} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/choose-plan" element={<Protected role="founder"><ChoosePlan /></Protected>} />
            <Route path="/redeem" element={<RedeemAccess />} />
            <Route path="/redeem/:code" element={<RedeemAccess />} />
            <Route path="/sign/:token" element={<SignDocument />} />
            <Route path="/u/:token" element={<SharedUpdate />} />
            <Route path="/verify/:token" element={<VerifyBadge />} />
            <Route path="/d/:token" element={<DeckViewer />} />
            <Route path="/dr/:token" element={<PublicDataRoom />} />
            <Route path="/dd/:token" element={<PublicDiligence />} />

            <Route path="/app" element={<Protected role="founder"><Dashboard /></Protected>} />
            <Route path="/app/metrics" element={<Protected role="founder"><Metrics /></Protected>} />
            <Route path="/app/structure" element={<Protected role="founder"><StructureNotes /></Protected>} />
            <Route path="/app/updates" element={<Protected role="founder"><UpdatesList /></Protected>} />
            <Route path="/app/updates/new" element={<Protected role="founder"><UpdateComposer /></Protected>} />
            <Route path="/app/updates/:id/edit" element={<Protected role="founder"><UpdateComposer /></Protected>} />
            <Route path="/app/investors" element={<Protected role="founder"><Investors /></Protected>} />
            <Route path="/app/delivery" element={<Protected role="founder"><Delivery /></Protected>} />
            <Route path="/app/settings" element={<Protected role="founder"><Settings /></Protected>} />
            <Route path="/app/documents" element={<Protected role="founder"><Documents /></Protected>} />
            <Route path="/app/templates" element={<Protected role="founder"><Templates /></Protected>} />
            <Route path="/app/integrations" element={<Protected role="founder"><Integrations /></Protected>} />
            <Route path="/app/requests" element={<Protected role="founder"><Requests /></Protected>} />
            <Route path="/app/document-builder" element={<Protected role="founder"><DocumentBuilder /></Protected>} />
            <Route path="/app/document-builder/:id" element={<Protected role="founder"><DocumentBuilderEditor /></Protected>} />
            <Route path="/app/signatures" element={<Protected role="founder"><SignerDashboard /></Protected>} />
            <Route path="/app/investor-impact" element={<Protected role="founder"><InvestorImpact /></Protected>} />
            <Route path="/app/sweat-equity" element={<Protected role="founder"><SweatEquity /></Protected>} />
            <Route path="/app/billing" element={<Protected role="founder"><Billing /></Protected>} />
            <Route path="/app/fundraising" element={<Protected role="founder"><Fundraising /></Protected>} />
            <Route path="/app/directory" element={<Protected role="founder"><InvestorDirectory /></Protected>} />
            <Route path="/app/cap-table" element={<Protected role="founder"><CapTable /></Protected>} />
            <Route path="/app/deck-links" element={<Protected role="founder"><DeckLinks /></Protected>} />
            <Route path="/app/rounds" element={<Protected role="founder"><Rounds /></Protected>} />
            <Route path="/app/data-rooms" element={<Protected role="founder"><DataRooms /></Protected>} />
            <Route path="/app/asks" element={<Protected role="founder"><AsksQA /></Protected>} />
            <Route path="/app/team" element={<Protected role="founder"><Team /></Protected>} />
            <Route path="/app/funds" element={<Protected role="founder"><Funds /></Protected>} />
            <Route path="/app/funds/:id" element={<Protected role="founder"><FundDetail /></Protected>} />
            <Route path="/app/diligence" element={<Protected role="founder"><DiligenceRoom /></Protected>} />
            <Route path="/app/advisor" element={<Protected role="founder"><Advisor /></Protected>} />

            <Route path="/portal" element={<Protected role="investor"><Portfolio /></Protected>} />
            <Route path="/portal/funds" element={<Protected role="investor"><FundPositions /></Protected>} />
            <Route path="/portal/funds/:lpId" element={<Protected role="investor"><FundPositionDetail /></Protected>} />
            <Route path="/portal/impact" element={<Protected role="investor"><ImpactHeatmap /></Protected>} />
            <Route path="/portal/company/:id" element={<Protected role="investor"><CompanyHealth /></Protected>} />
            <Route path="/portal/updates/:id" element={<Protected role="investor"><UpdateReaderPage /></Protected>} />
            <Route path="/portal/account" element={<Protected role="investor"><InvestorAccount /></Protected>} />

            <Route path="/admin" element={<Protected role="admin"><AdminDashboard /></Protected>} />

            <Route path="/pricing" element={<Pricing />} />
            <Route path="/product/:slug" element={<ProductDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/fund-managers" element={<FundManagers />} />
            <Route path="/lp-invite/:token" element={<LpInvite />} />
            <Route path="/tour" element={<Tour />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/docs" element={<Docs />} />
            <Route path="/guides" element={<Guides />} />
            <Route path="/guides/:slug" element={<GuideDetail />} />
            <Route path="/security" element={<Security />} />
            <Route path="/tools/option-calculator" element={<OptionCalculator />} />
            <Route path="/equity/:token" element={<EmployeeEquity />} />
            <Route path="/status" element={<Status />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />

            <Route path="/" element={<Landing />} />
            <Route path="*" element={<RootRedirect />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors mobileOffset={{ top: 72 }} />
      </AuthProvider>
    </div>
  );
}

export default App;
